<template>
  <div id="chatter-snippet-form">
    <!-- Spinner -->
    <loading :active.sync="loading"></loading>
    <!-- /Spinner -->

    <b-card>
      <!-- Form -->
      <b-form>
        <b-form-row>
          <!-- Process name -->
          <b-col>
            <label for="name">Process Name <span class="required">*</span></label>
            <b-form-input id="name"
                          name="name"
                          type="text"
                          v-validate="{ required: true, regex: /^[a-zA-Z0-9_]+$/ }"
                          data-vv-as="process name"
                          :state="validateState('name')"
                          v-model="process.processName"></b-form-input>
            <b-form-invalid-feedback id="name-feedback">
              {{ errors.first('name') }}
            </b-form-invalid-feedback>
          </b-col>
          <!-- /Process name -->
        </b-form-row>

        <b-form-row>
          <!-- Version -->
          <b-col>
            <label for="version">Version <span class="required">*</span></label>
            <b-form-input id="version"
                          name="version"
                          version="version"
                          type="text"
                          v-validate="{ required: true }"
                          placeholder="e.g. 1.1.10"
                          :state="validateState('version')"
                          v-model="process.version"></b-form-input>
            <b-form-invalid-feedback id="version-feedback">
              {{ errors.first('version') }}
            </b-form-invalid-feedback>
          </b-col>
          <!-- /Version -->
          <!-- Starting Point -->
          <b-col>
            <label for="startingPoint">Starting Point <span class="required">*</span></label>
            <b-form-input id="startingPoint"
                          name="startingPoint"
                          type="number"
                          v-validate="{ required: true }"
                          data-vv-as="starting point"
                          :state="validateState('startingPoint')"
                          v-model="process.startingPoint"></b-form-input>
            <b-form-invalid-feedback id="startingPoint-feedback">
              {{ errors.first('startingPoint') }}
            </b-form-invalid-feedback>
          </b-col>
          <!-- /Version -->
        </b-form-row>

        <!-- Active -->
        <b-form-group>
          <label for="active">Active</label>
          <b-form-checkbox id="active"
                           name="active"
                           size="lg"
                           v-model="process.active"
                           :state="validateState('active')">
          </b-form-checkbox>
        </b-form-group>
        <!-- /Active -->

        <!-- Description -->
        <b-form-group>
          <label for="description">Description</label>
          <b-form-textarea id="description"
                           name="description"
                           rows="6"
                           v-model="process.description">
          </b-form-textarea>
        </b-form-group>
        <!-- /Description -->

        <!-- File -->
        <b-form-group label="Add a file"
                      label-for="file">
          <b-form-file ref="file"
                       id="file"
                       name="file"
                       v-model="file"
                       @input="addFile"
                       :state="validateState('files')"
                       placeholder="Choose a file or drop it here"
                       drop-placeholder="Drop a file here"></b-form-file>
          <b-form-input v-show="false"
                        name="files"
                        data-vv-as="files"
                        v-validate="{ positive: true }"
                        :state="validateState('files')"
                        v-model="fileNo"></b-form-input>
          <b-form-invalid-feedback id="files-feedback">
            {{ errors.first('files') }}
          </b-form-invalid-feedback>
        </b-form-group>
        <!-- /File -->

        <br>

        <!-- File list -->
        <b-form-group v-if="showSnippets()">
          <label>Files</label>
          <template v-for="snippet in process.snippetList">
            <div v-if="!snippet.deleted"
                 :key="snippet.file"
                 class="d-flex justify-content-between align-items-center">
              <span>{{ snippet.file }}</span>
              <div>
                <b-badge v-if="!snippet.new"
                         class="btn mr-2"
                         variant="primary"
                         @click="downloadFile(snippet)">Download
                </b-badge>
                <b-badge variant="danger"
                         class="btn"
                         @click="deleteFile(snippet)">Delete
                </b-badge>
              </div>
            </div>
          </template>
        </b-form-group>
        <!-- /File list -->
      </b-form>
      <!-- /Form -->

      <br>

      <!-- Buttons -->
      <div class="ml-3">
        <b-button v-if="create"
                  @click="submit"
                  variant="primary"
                  class="mr-1">Submit
        </b-button>
        <b-button v-else
                  @click="update"
                  variant="primary"
                  class="mr-1">Update
        </b-button>
        <b-button type="button"
                  @click="cancel">Cancel
        </b-button>
      </div>
      <!-- /Buttons -->
    </b-card>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import { Validator } from 'vee-validate';

Validator.extend('positive', {
  getMessage: field => `The ${field} field is required.`,
  validate: value => value > 0
});

export default ({
  components: { Loading },
  props: ['processName', 'version'],
  computed: {
    fileNo() {
      return this.process.snippetList.filter(snippet => !snippet.deleted).length;
    }
  },
  async mounted() {
    if (this.processName && this.version) {
      await this.load();
    } else {
      this.create = true;
    }
  },
  data() {
    return {
      process: {
        processName: '',
        version: '',
        startingPoint: null,
        active: false,
        description: '',
        snippetList: [],
      },
      create: false,
      file: null,
      loading: false,
    }
  },
  methods: {
    async load() {
      try {
        let response = await axios.get('/code/retrieveSnippet', {
          headers: { Authorization: accessToken() },
          params: {
            processName: this.processName,
            version: this.version
          }
        });

        response.data.snippetList.forEach(snippet => snippet.deleted = false);

        this.process = response.data;
      } catch (error) {
        this.loading = false;
        this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      let result = await this.$validator.validate();
      if (!result) return;

      try {
        this.loading = true;

        // Create formData
        let data = new FormData();
        data.append('processName', this.process.processName);
        data.append('version', this.process.version);
        data.append('startingPoint', this.process.startingPoint);
        data.append('active', this.process.active);
        data.append('description', this.process.description);
        this.process.snippetList.forEach(snippet => !snippet.deleted && data.append('files', snippet.data));

        // Submit a new chat
        await axios.post('/code/createSnippetList', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: accessToken()
          }
        });

        this.loading = false;
        await this.showMessage('생성이 완료되없습니다');
        this.pushRoute(`/chatter/code/snippets/form?processName=${process.processName}&version=${process.version}`);
      } catch (error) {
        this.loading = false;
        this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async update() {
      let result = await this.$validator.validate();
      if (!result) return;

      // Update the chat
      try {
        // Create formData
        let data = new FormData();
        data.append('processName', this.process.name);
        data.append('version', this.process.version);
        data.append('active', this.process.active);
        data.append('description', this.process.description);
        this.process.files.forEach(file => file.new && !file.deleted && data.append('files', file.file));

        await axios.post('chatter/updateChatter/' + this.process.chatId,
          data,
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'multipart/form-data',
              Authorization: accessToken()
            }
          });

        // Delete files
        data = new FormData();
        let fileIds = [];
        this.chat.files.forEach(file => {
          // Delete if the file already exists in DB and has been deleted
          !file.new && file.deleted && fileIds.push(file.attachedFileId);
        });
        data.append('fileIds', fileIds);

        if (fileIds.length > 0) {
          await axios.post('chatter/deleteChatterFile', data, {
            headers: {
              Authorization: accessToken()
            }
          });
        }
      } catch (error) {
        this.showError(error.message);
      }
    },
    async addFile() {
      if (!this.file) return;

      // Clone the file input
      const original = this.file;
      const clone = new File([original], original.name, {
        type: original.type,
        lastModified: original.lastModified,
      });

      // Push the file into the array in process object
      this.process.snippetList.push({
        data: clone,
        file: clone.name,
        new: true,
        deleted: false,
      });

      // Reset
      this.$refs['file'].reset();
    },
    async downloadFile() {

    },
    deleteFile(snippet) {
      snippet.deleted = true;
    },
    validateState(ref) {
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.errors.has(ref);
      }
      return null;
    },
    showSnippets() {
      return this.process.snippetList && this.process.snippetList.length > 0 &&
        this.process.snippetList.some(snippet => !snippet.deleted);
    },
    cancel() {
      this.pushRoute('/chatter/code/snippets');
    }
  }
})
</script>

<style>
span.required {
    color: red;
}
</style>

